/**
 * Keyboard helpers shared by the shell and the screens: the ⌘↵ primary-bulk-action event and the
 * "/" page-filter shortcut (design MC:1301-1323).
 */
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { isAnyLayerOpen } from './layers';

/** Window CustomEvent fired by ⌘↵ / Ctrl+↵; the current screen runs its primary bulk action. */
export const PRIMARY_BULK_ACTION_EVENT = 'primary-bulk-action';

/** The key that focuses the page's filter input (shown as the "/" key cap). */
export const PAGE_FILTER_KEY = '/';

export function isCommandKey(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey;
}

/** Input types that do not take typed text: page shortcuts still work while they have focus. */
const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file']);

/** True when the event target takes typed input (design: page keys are ignored there). */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(target.type);
  return target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

export function dispatchPrimaryBulkAction(): void {
  window.dispatchEvent(new CustomEvent(PRIMARY_BULK_ACTION_EVENT));
}

/** Screens call this to run their primary bulk action on ⌘↵. */
export function usePrimaryBulkAction(handler: () => void, enabled = true): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    const listener = () => handlerRef.current();
    window.addEventListener(PRIMARY_BULK_ACTION_EVENT, listener);
    return () => window.removeEventListener(PRIMARY_BULK_ACTION_EVENT, listener);
  }, [enabled]);
}

function isPlainFilterKey(event: KeyboardEvent): boolean {
  return event.key === PAGE_FILTER_KEY && !event.metaKey && !event.ctrlKey && !event.altKey;
}

/**
 * "/" focuses and selects the page's filter input. Ignored while typing in another field, when
 * an overlay (drawer, dialog, menu, palette) is open, or when the input is disabled or hidden.
 * `FilterInput` with `shortcut="/"` registers itself; custom filters pass their own ref.
 */
export function usePageFilterShortcut(ref: RefObject<HTMLInputElement | null>, enabled = true): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isPlainFilterKey(event) || event.defaultPrevented) return;
      if (isEditableTarget(event.target) || isAnyLayerOpen()) return;
      const input = ref.current;
      if (!input || input.disabled || !input.isConnected) return;
      event.preventDefault();
      input.focus();
      input.select();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [ref, enabled]);
}
